import { useState, useEffect } from 'react';
import axios from 'axios';
import { getToken } from '../../utils/auth';

const api = axios.create({ baseURL: '/api', timeout: 20000 });
const auth = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export default function NewsModal({ item, onClose }) {
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get(`/market/article?url=${encodeURIComponent(item.link)}`, auth())
      .then(r => setContent(r.data))
      .catch(e => setError(e.response?.data?.error || 'Could not load article'))
      .finally(() => setLoading(false));
  }, [item.link]);

  // Close on Escape
  useEffect(() => {
    const handler = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  const timeAgo = (dateStr) => {
    const m = Math.floor((Date.now() - new Date(dateStr)) / 60000);
    if (m < 60) return `${m}m ago`;
    if (m < 1440) return `${Math.floor(m / 60)}h ago`;
    return `${Math.floor(m / 1440)}d ago`;
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000,
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: 'var(--bg-card)', borderRadius: 12, width: '100%', maxWidth: 680,
          maxHeight: '88vh', display: 'flex', flexDirection: 'column',
          border: '1px solid var(--border)', boxShadow: '0 24px 64px rgba(0,0,0,0.5)',
        }}
      >
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em', flexShrink: 0 }}>
              {item.publisher}
            </span>
            <span style={{ fontSize: 11, color: 'var(--text-muted)', flexShrink: 0 }}>· {timeAgo(item.publishedAt)}</span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', fontSize: 18, lineHeight: 1, flexShrink: 0, padding: '2px 6px' }}
          >✕</button>
        </div>

        {/* Scrollable body */}
        <div style={{ overflowY: 'auto', padding: '20px 24px', flex: 1 }}>
          {loading && (
            <div>
              <div className="skeleton" style={{ height: 28, borderRadius: 6, marginBottom: 12 }} />
              <div className="skeleton" style={{ height: 20, borderRadius: 6, width: '60%', marginBottom: 24 }} />
              <div className="skeleton" style={{ height: 200, borderRadius: 8, marginBottom: 20 }} />
              {[1,2,3,4].map(i => (
                <div key={i} className="skeleton" style={{ height: 14, borderRadius: 4, marginBottom: 10, width: i % 2 === 0 ? '85%' : '100%' }} />
              ))}
            </div>
          )}

          {error && (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ fontSize: 32, marginBottom: 12 }}>⚠️</div>
              <div style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 20 }}>{error}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: 13, marginBottom: 20 }}>{item.title}</div>
              <a href={item.link} target="_blank" rel="noopener noreferrer"
                style={{ display: 'inline-block', padding: '10px 24px', background: 'var(--accent)', color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 13, fontWeight: 600 }}>
                Open in New Tab ↗
              </a>
            </div>
          )}

          {content && !loading && (
            <>
              {/* Hero image */}
              {content.image && (
                <img src={content.image} alt=""
                  style={{ width: '100%', maxHeight: 260, objectFit: 'cover', borderRadius: 8, marginBottom: 20 }}
                  onError={e => e.target.style.display = 'none'} />
              )}

              {/* Title */}
              <h2 style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.35, marginBottom: 12 }}>
                {content.title || item.title}
              </h2>

              {/* Description */}
              {content.description && (
                <p style={{ fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.6, marginBottom: 20, fontStyle: 'italic', borderLeft: '3px solid var(--accent)', paddingLeft: 12 }}>
                  {content.description}
                </p>
              )}

              {/* Article body */}
              {content.paragraphs?.length > 0 ? (
                <div>
                  {content.paragraphs.map((p, i) => (
                    <p key={i} style={{ fontSize: 14, color: 'var(--text-primary)', lineHeight: 1.75, marginBottom: 14 }}>
                      {p}
                    </p>
                  ))}
                </div>
              ) : (
                <p style={{ color: 'var(--text-muted)', fontSize: 13 }}>
                  Full article content could not be extracted. Click below to read on the original site.
                </p>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        <div style={{ padding: '12px 24px', borderTop: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexShrink: 0 }}>
          <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>
            Source: {content?.siteName || item.publisher}
          </span>
          <a href={item.link} target="_blank" rel="noopener noreferrer"
            style={{ fontSize: 12, fontWeight: 600, color: 'var(--accent)', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: 4 }}>
            Read full article ↗
          </a>
        </div>
      </div>
    </div>
  );
}
